<template>
    <div class="navigation-drawer__navlist-item"
         @mouseenter="mouseOverMenu($event)"
         @mouseleave="mouseOffMenu($event)">
        <v-list-item
                class="navigation-drawer__navlist-tile"
                @click="clickMenuItem"
        >
            <v-list-item-title>
                <v-icon :class="iconColor">
                    {{ menuItem.icon }}
                </v-icon>
                <h3 class="body-2" :class="iconColor">
                    {{ menuItem.label }}
                </h3>
                <v-icon v-if="hasChildren" :class="iconColor">
                    keyboard_arrow_right
                </v-icon>
            </v-list-item-title>
        </v-list-item>
        <!-- Begin: Was Outer -->
        <!--
            Visibility is controlled by the parent (Navigation_Drawer) via the
            activeMenuId prop, not by per-instance openState data. Using a
            one-way :model-value bound to a computed makes activeMenuId the
            single source of truth, so stale per-instance state can never
            leave two submenus open at once.
        -->
        <v-menu v-if="hasChildren && drawOpenUi"
                :open-on-click="false"
                :open-on-hover="false"
                :close-on-click="false"
                transition="slide-y-transition"
                content-class="navigation-drawer__navlist-flyout"
                :model-value="isOpen"
                :activator="myActivator"
                location="end"
                :offset="1"
        >
            <v-list density="compact" theme="light" ref="navigation-drawer-flyout"
                    @mouseenter="mouseOverMenu($event)"
                    @mouseleave="mouseOffMenu($event)">
                <v-list-item v-for="child in filteredChildren(menuItem.children)"
                             :key="generateUniqueHash(child)"
                             @click="clickSubMenuItem(child)"
                >
                    <v-list-item-title>{{ child.label }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>
        <!-- End: Was Outer -->
    </div>
</template>


<script>
    import {mapActions, mapState, mapGetters} from 'vuex'

    import {
        VUEX_UI_HIDE_NAVIGATION_DRAWER
    } from '@/store/constants/ui'

    // Module-level counter so every menu item instance gets a guaranteed
    // unique id. The previous `Date.now()` approach returned the same value
    // for items mounted in the same v-for tick, which collapsed the
    // activeMenuId watcher (every item saw `newId === this.id` and skipped
    // hideSubMenu), leaving multiple submenus visible at once.
    let menuItemIdSeq = 0

    export default {
        name: 'navigation-drawer-item',

        data: () => ({
            iconColor: 'text-grey-darken-2',
            id: ++menuItemIdSeq,
            isMenuHovered: false,
            myActivator: null,
            timer: null,
        }),

        props: [
            'menuItem',
            'isDrawerOpen',
            'activeMenuId'
        ],

        computed: {
            ...mapGetters({
                currentUserHasPerm: 'currentUserHasPerm'
            }),

            ...mapState({
                drawOpenUi: state => state.ui.navigationDrawer.openState
            }),
            hasChildren() {
                return (this.menuItem.children && this.menuItem.children.length)
            },

            // Submenu visibility derives from the parent's activeMenuId,
            // not local state. There is no per-item openState data to
            // desync — when activeMenuId changes, every menu item's isOpen
            // recomputes and the v-menu's :model-value updates in lockstep.
            isOpen() {
                return this.activeMenuId === this.id
            },

            ...mapState({
                currentUser: state => state.auth.currentUser
            })
        },

        mounted() {
            this.myActivator = this.$el.querySelector('.navigation-drawer__navlist-tile')
        },

        watch: {
            isDrawerOpen: function (isOpen) {
                if (this.isMenuHovered && isOpen) {
                    this.showSubMenu()
                } else {
                    this.hideSubMenu()
                }
            }
        },

        methods: {
            ...mapActions({
                closeDrawer: VUEX_UI_HIDE_NAVIGATION_DRAWER
            }),

            hasPermission(childItem) {
                let userPerms = this.currentUser.permissions

                if (childItem !== 'Requests') return true
                return userPerms.includes(SILA_PERMISSION_JOBS_JOB_CREATE)
            },

            filteredChildren(children) {
                const navItems = children.filter(navItem => {
                    const permission = navItem.permission

                    if (permission && permission !== '' && this.currentUserHasPerm(window[permission]) === false) {
                        return false
                    }

                    if (!this.hasPermission(navItem.label) && navItem.label === 'Requests') {
                        return false
                    }
                    return true
                })
                return navItems
            },

            clickMenuItem() {
                if (!this.hasChildren) {
                    this.$router.push({name: this.menuItem.routeName})
                    this.hideSubMenu()
                    // Tell the parent to close every sibling's submenu too.
                    // Otherwise a sibling whose openState is still true
                    // (e.g. previously hovered) will re-appear next time
                    // the drawer opens.
                    this.$emit('menu-clicked')
                    // Collapse the side nav after navigating so the drawer
                    // doesn't stay open over the destination page.
                    this.closeDrawer()
                }
            },

            clickSubMenuItem(item) {
                this.hideSubMenu()
                // Broadcast clear so any other open submenu in the nav
                // also resets. Without this, the previously-clicked
                // submenu can stay openState=true and re-render on the
                // next drawer-open.
                this.$emit('menu-clicked')
                // Collapse the side nav immediately on flyout-item click.
                this.closeDrawer()
                this.$router.push({name: item.routeName})
            },

            switchMenuIconColor(e) {
                this.iconColor = (e.type === 'mouseenter') ? 'text-white' : 'text-grey-darken-2'
            },

            mouseOverMenu(e) {
                clearTimeout(this.timer)
                this.switchMenuIconColor(e)
                this.isMenuHovered = true
                this.showSubMenu()
            },

            mouseOffMenu(e) {
                clearTimeout(this.timer)
                this.timer = window.setTimeout(() => {
                    this.switchMenuIconColor(e)
                    this.isMenuHovered = false
                    this.$emit('menu-hoveroff', this.id)
                    this.hideSubMenu()
                }, 50)
            },

            showSubMenu() {
                // Single emit point. Parent updates activeMenuId; every
                // menu item's isOpen computed reacts so exactly one
                // submenu is visible at a time.
                if (this.hasChildren && this.drawOpenUi) {
                    this.$emit('menu-hover', this.id)
                }
            },

            hideSubMenu() {
                // Only clear if we're the currently-active item. A late
                // mouseleave timer on a non-active item must not clobber
                // the sibling the user already moved to.
                if (this.activeMenuId === this.id) {
                    this.$emit('menu-clear')
                }
            },

        },
    }
</script>

